import React, { useState } from 'react';
import RaceRow from './RaceRow';
import { EventGroup } from '../types';

interface ResultsTableProps {
  allEvents: EventGroup[];
  isLive?: boolean;
  toggleMode?: string;
}

const ResultsTable: React.FC<ResultsTableProps> = ({
  allEvents,
  isLive = false,
  toggleMode = 'static',
}) => {
  const [hoveredSharedRace, setHoveredSharedRace] = useState<string | null>(
    null
  );

  if (!allEvents || allEvents.length === 0) {
    return (
      <div className='p-4 text-center text-text-secondary-light dark:text-text-secondary-dark'>
        No race results to display.
      </div>
    );
  }

  const headerCellClasses =
    'px-3 py-3 text-center text-xs font-medium uppercase tracking-wider text-text-primary-light dark:text-white font-mono';

  // Generate a unique key for each shared race group
  const getSharedRaceKey = (row: any) => {
    if (!row.isSharedRace) return undefined;
    return `${row.event}-${row.raceNumber}-${row.marketStartTime}`;
  };

  const handleSharedRaceHover = (raceKey: string | null) => {
    setHoveredSharedRace(raceKey);
  };

  // Format event name without race time
  const formatEventName = (eventGroup: EventGroup) => {
    return eventGroup.location;
  };

  return (
    <div className='space-y-6'>
      {allEvents.map((eventGroup, groupIndex) => {
        // Track alternating index for the entire event group
        let alternatingIndex = -1; // Start at -1 so first increment gives us 0

        return (
          <div
            key={eventGroup.location || groupIndex}
            className='relative bg-surface-light dark:bg-surface-dark rounded-lg shadow-lg ring-1 ring-border-light dark:ring-border-dark overflow-hidden'
          >
            {/* Location Header */}
            <div className='px-6 py-4 bg-white dark:bg-black border-b border-border-light dark:border-border-dark'>
              <h3 className='text-lg font-semibold text-black dark:text-white'>
                {formatEventName(eventGroup)}
              </h3>
            </div>

            {/* Table */}
            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-border-light dark:divide-border-dark'>
                <thead className='bg-hover-light/50 dark:bg-black/50'>
                  <tr>
                    {/* Start Time */}
                    <th scope='col' className={headerCellClasses}>
                      <span className='hidden sm:inline'>Start Time</span>
                      <span className='sm:hidden'>Start</span>
                    </th>

                    {/* Race # */}
                    <th scope='col' className={headerCellClasses}>
                      <span className='hidden sm:inline'>Race #</span>
                      <span className='sm:hidden'>#</span>
                    </th>

                    {/* Jockey */}
                    <th scope='col' className={headerCellClasses}>
                      Jockey
                    </th>

                    {/* Runner - Hidden on mobile */}
                    <th
                      scope='col'
                      className={`${headerCellClasses} hidden sm:table-cell`}
                    >
                      Runner
                    </th>

                    {/* Back Odds - Hidden on mobile, replaced by combined Odds */}
                    <th
                      scope='col'
                      className={`${headerCellClasses} hidden sm:table-cell`}
                    >
                      Back Odds
                    </th>

                    {/* Lay Odds - Hidden on mobile, replaced by combined Odds */}
                    <th
                      scope='col'
                      className={`${headerCellClasses} hidden sm:table-cell`}
                    >
                      Lay Odds
                    </th>

                    {/* Combined Back - Hidden on mobile, replaced by combined Comb. */}
                    <th
                      scope='col'
                      className={`${headerCellClasses} hidden sm:table-cell`}
                    >
                      Comb. Back
                    </th>

                    {/* Combined Lay - Hidden on mobile, replaced by combined Comb. */}
                    <th
                      scope='col'
                      className={`${headerCellClasses} hidden sm:table-cell`}
                    >
                      Comb. Lay
                    </th>

                    {/* Combined Combined - Mobile only */}
                    <th
                      scope='col'
                      className={`${headerCellClasses} sm:hidden`}
                    >
                      Comb.
                    </th>

                    {/* Liquidity */}
                    <th scope='col' className={headerCellClasses}>
                      <span className='hidden sm:inline'>Liquidity</span>
                      <span className='sm:hidden'>Liq.</span>
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-surface-light dark:bg-surface-dark divide-y divide-border-light dark:divide-border-dark'>
                  {eventGroup.rows.map((row, rowIndex) => {
                    const sharedRaceKey = getSharedRaceKey(row);
                    const isSharedRaceHovered =
                      sharedRaceKey === hoveredSharedRace;

                    // Only increment alternating index for first row in shared race group
                    if (!row.isSharedRace || row.isFirstInSharedRace) {
                      alternatingIndex++;
                    }

                    return (
                      <RaceRow
                        key={`${row.event}-${row.raceNumber}-${row.jockey}-${row.runner}-${rowIndex}`}
                        row={row}
                        isEvenRow={rowIndex % 2 === 0}
                        sharedRaceKey={sharedRaceKey}
                        onSharedRaceHover={handleSharedRaceHover}
                        isSharedRaceHovered={isSharedRaceHovered}
                        alternatingIndex={alternatingIndex}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ResultsTable;
